import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { RawInput } from '@ps/web-shared-components';
import classNames from 'clsx';
import { identity as parse } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import ArrowIcon from '@swf/core/assets/icons/arrow.svg';
import { select } from '@swf/core/store';
import type { FieldNameProp } from '@swf/core/types/Props';

import useDisableField from '../../hooks/useDisableField';
import { getCountryStates } from '../../utils/country-states/country-states';
import StateSelect from '../StateSelect/StateSelect';
// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import { label, root } from '../TextInput/TextInput.scss';

import {
  TXTCLI_Community,
  TXTCLI_County,
  TXTCLI_Periphery,
  TXTCLI_PleaseX_select_province,
  TXTCLI_PleaseX_select_state,
  TXTCLI_Please_select_State___Province_ELL,
  TXTCLI_Please_select_community,
  TXTCLI_Province,
  TXTCLI_Region,
  TXTCLI_State_X_District,
  TXTCLI_State_X_Territory,
  TXTCLI_State_territory,
  TXTWCL_District,
  TXTWCL_Please_select_CountyX,
  TXTWCL_Please_select_PeripheryX,
  TXTWCL_Please_select_RegionX,
  TXTWCL_Please_select_State_Union_Terr_ELL,
  TXTWCL_Please_select_State___District_ELL,
  TXTWCL_Please_select_State___Territor_ELL,
  TXTWCL_Please_select_your_District,
  TXTWCL_State_Union_Territory,
  TXTWCL_State___Province
} from './State.messages.json';
import { formControl, rmfDisabled, selectClass } from './State.scss';

interface Props extends FieldNameProp {
  country: string;
  state?: string;
}

const stateLabel: Record<string, TrackedTranslation> = {
  AU: TXTCLI_State_X_Territory,
  BE: TXTCLI_Province,
  BR: TXTCLI_State_X_District,
  CA: TXTCLI_Province,
  CZ: TXTCLI_Region,
  DE: TXTCLI_State_territory,
  DEFAULT: TXTWCL_State___Province,
  ES: TXTCLI_Community,
  GR: TXTCLI_Periphery,
  IE: TXTCLI_County,
  IN: TXTWCL_State_Union_Territory,
  IT: TXTCLI_Province,
  PT: TXTWCL_District,
  US: TXTCLI_State_territory
};

const stateMsg: Record<string, TrackedTranslation> = {
  AU: TXTWCL_Please_select_State___Territor_ELL,
  BE: TXTCLI_PleaseX_select_province,
  BR: TXTWCL_Please_select_State___District_ELL,
  CA: TXTCLI_PleaseX_select_province,
  CZ: TXTWCL_Please_select_RegionX,
  DE: TXTCLI_PleaseX_select_state,
  DEFAULT: TXTCLI_Please_select_State___Province_ELL,
  ES: TXTCLI_Please_select_community,
  GR: TXTWCL_Please_select_PeripheryX,
  IE: TXTWCL_Please_select_CountyX,
  IN: TXTWCL_Please_select_State_Union_Terr_ELL,
  IT: TXTCLI_PleaseX_select_province,
  PT: TXTWCL_Please_select_your_District,
  US: TXTCLI_PleaseX_select_state
};

export const findState = (lookupValue: string, states: { value: string; text: string }[]) =>
  states.find(({ value }) => value === lookupValue);

const State: React.FC<Props> = ({ country, fieldName, state = '' }) => {
  const disableField = useDisableField();
  const {
    input: { onChange: stateChange }
  } = useField(fieldName, { subscription: { value: true }, parse });
  const locale = useSelector(select.locale.get);
  const siteId = useSelector(select.cms.getSiteId);
  const countryStates = getCountryStates(country, { siteId, locale });
  const prevCountryRef = useRef(country);
  const labelString = stateLabel[country] || stateLabel.DEFAULT;
  const msgString = stateMsg[country] || stateMsg.DEFAULT;

  useEffect(() => {
    const prevCountry = prevCountryRef.current;

    if (prevCountry !== country) {
      prevCountryRef.current = country;
      stateChange('');
    }
  }, [country, stateChange]);

  if (!countryStates.length) {
    return null;
  }

  return (
    <StateSelect
      country={country}
      name={fieldName}
      validatorTranslation={stateMsg.DEFAULT}
      label={{ ...labelString }}
      title={msgString}
      initialValue={findState(state, countryStates)?.text ?? ''}
      disabled={disableField(fieldName)}
      className={selectClass}
    >
      {({ input, meta }) => (
        <RawInput
          input={input}
          meta={meta}
          id={fieldName}
          label={labelString}
          labelClassName={label}
          rawValue={findState(input.value, countryStates)?.text ?? ''}
          className={classNames(root, formControl, {
            [rmfDisabled]: disableField(fieldName)
          })}
          showValidationTick={false}
          readOnly
        >
          <ArrowIcon />
        </RawInput>
      )}
    </StateSelect>
  );
};

export default State;
